import React, { useEffect, useState } from 'react';
import { ClockIcon, AlertTriangleIcon } from 'lucide-react';
const EXPIRED_MINUTES = 5; // Show "expired" for 5 minutes after midnight
type TimerPhase = 'active' | 'expired';
interface TimerState {
  phase: TimerPhase;
  remainingSeconds: number;
}
interface CountdownTimerProps {
  className?: string;
}
function getTimerState(): TimerState {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  // Check if we're in the expired window (00:00 to 00:05)
  if (hours === 0 && minutes < EXPIRED_MINUTES) {
    const expiredSecondsLeft = (EXPIRED_MINUTES - minutes) * 60 - seconds;
    return {
      phase: 'expired',
      remainingSeconds: expiredSecondsLeft
    };
  }
  // Calculate seconds until midnight
  let targetHour = 24;
  // If we're past the expired window (00:05+), count down to next midnight
  const secondsSinceMidnight = hours * 3600 + minutes * 60 + seconds;
  const secondsUntilMidnight = 24 * 3600 - secondsSinceMidnight;
  return {
    phase: 'active',
    remainingSeconds: secondsUntilMidnight
  };
}
// Clean up old localStorage keys from previous versions
function cleanupOldStorage() {
  localStorage.removeItem('countdown_cycle_start');
  localStorage.removeItem('countdown_cycle_start_v2');
}
export function CountdownTimer({
  className = ''
}: CountdownTimerProps) {
  const [state, setState] = useState<TimerState>(() => getTimerState());
  useEffect(() => {
    cleanupOldStorage();
    const timerId = setInterval(() => {
      setState(getTimerState());
    }, 1000);
    return () => clearInterval(timerId);
  }, []);
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  if (state.phase === 'expired') {
    return <div className={`flex items-center justify-center space-x-2 font-semibold bg-red-100 text-red-700 py-2 px-4 rounded-lg border border-red-300 ${className}`} data-id="element-14">
        <AlertTriangleIcon className="w-5 h-5 animate-pulse" data-id="element-15" />
        <span className="text-lg tracking-wider font-body uppercase" data-id="element-16">
          Oferta Expirada
        </span>
      </div>;
  }
  return <div className={`flex items-center justify-center space-x-2 font-semibold bg-green-50 text-green-700 py-2 px-4 rounded-lg border border-green-200 ${className}`} data-id="element-17">
      <ClockIcon className="w-5 h-5 animate-pulse" data-id="element-18" />
      <span className="text-lg tracking-wider font-body" data-id="element-19">
        {formatTime(state.remainingSeconds)}
      </span>
    </div>;
}